import React, { useState } from "react";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";

const FormContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 8px;
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 32px;
  text-align: center;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const Label = styled.label`
  color: #374151;
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) =>
    props.$variant === "secondary"
      ? `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  `
      : `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 16px;
`;

const PasswordRequirements = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
`;

const RequirementTitle = styled.h4`
  color: #374151;
  font-size: 0.9rem;
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const RequirementList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #6b7280;
  font-size: 0.85rem;

  li {
    margin-bottom: 4px;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: underline;
  margin-top: 16px;

  &:hover {
    color: #5a67d8;
  }
`;

interface CreateAdminFormProps {
  onSwitchToLogin?: () => void;
}

const CreateAdminForm: React.FC<CreateAdminFormProps> = ({
  onSwitchToLogin,
}) => {
  const { createAdmin } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    setupSecret: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
    setSuccess("");
  };

  // Mirrors validateSetupPassword() on the backend: 12+ chars and at least
  // three of the four character classes. Kept in sync intentionally — the
  // server is authoritative, this is only for immediate feedback.
  const validatePassword = (password: string): boolean => {
    if (password.length < 12) return false;
    const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) =>
      re.test(password)
    ).length;
    return classes >= 3;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError("Nome e cognome sono obbligatori");
      setLoading(false);
      return;
    }

    if (!validatePassword(formData.password)) {
      setError("La password non soddisfa i requisiti");
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Le password non corrispondono");
      setLoading(false);
      return;
    }

    const result = await createAdmin({
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      setupSecret: formData.setupSecret,
    });

    if (result.success) {
      setSuccess("Account amministratore creato con successo! Ora sei loggato.");
    } else {
      setError(result.message || "Impossibile creare l'account amministratore");
    }

    setLoading(false);
  };

  return (
    <FormContainer>
      <Title>Configura Account Amministratore</Title>
      <Subtitle>
        Crea il primo account amministratore per Lead Me. Questo account avrà privilegi completi di sistema.
      </Subtitle>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <Form onSubmit={handleSubmit}>
        <InputRow>
          <InputGroup>
            <Label htmlFor="firstName">Nome</Label>
            <Input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Mario"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="lastName">Cognome</Label>
            <Input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Rossi"
              required
            />
          </InputGroup>
        </InputRow>

        <InputGroup>
          <Label htmlFor="email">Indirizzo Email</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="admin@azienda.com"
            required
          />
        </InputGroup>

        <InputGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Crea una password sicura"
            required
          />
          <PasswordRequirements>
            <RequirementTitle>Requisiti Password:</RequirementTitle>
            <RequirementList>
              <li>Almeno 12 caratteri</li>
              <li>
                Almeno tre categorie tra: minuscole, maiuscole, numeri, simboli
              </li>
            </RequirementList>
          </PasswordRequirements>
        </InputGroup>

        <InputGroup>
          <Label htmlFor="confirmPassword">Conferma Password</Label>
          <Input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Conferma la tua password"
            required
          />
        </InputGroup>

        <InputGroup>
          <Label htmlFor="setupSecret">Setup secret</Label>
          <Input
            type="password"
            id="setupSecret"
            name="setupSecret"
            value={formData.setupSecret}
            onChange={handleChange}
            placeholder="Valore di SETUP_SECRET configurato sul backend"
            required
          />
        </InputGroup>

        <Button type="submit" disabled={loading}>
          {loading ? "⏳ Creazione Account..." : "👑 Crea Account Amministratore"}
        </Button>
      </Form>

      {onSwitchToLogin && (
        <LinkButton onClick={onSwitchToLogin}>
          🔑 Hai già un account? Accedi
        </LinkButton>
      )}
    </FormContainer>
  );
};

export default CreateAdminForm;
